import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
  ArrowRight, Users, Globe, Lightbulb, 
  Target, Sparkles, Quote, Check, MapPin, ChevronLeft, ChevronRight, Handshake,
  Mic, Network, Building2, Flag
} from "lucide-react";
import { NewsletterSignup } from "../components/NewsletterSignup";

// Partners Image
const PARTNERS_IMAGE = "/assets/images/partners/all-partners.png";

// Testimonial Images
const TESTIMONIAL_IMAGES = {
  jsmann: "/assets/images/testimonials/jsmann.jpg",
  waraich: "/assets/images/testimonials/waraich.jpg",
  keran: "/assets/images/testimonials/keran.webp",
  damodar: "/assets/images/testimonials/damodar.jpeg",
  archana: "/assets/images/testimonials/archana.jpeg",
  neeraj: "/assets/images/testimonials/neeraj.jpeg"
};

// Gallery images for carousel
const GALLERY_IMAGES = [
  { url: "/assets/images/gallery/gallery1.jpg", caption: "AcadElite Awards 2025 - Lamp Lighting", event: "AcadElite" },
  { url: "/assets/images/gallery/gallery2.jpg", caption: "EdVantage - Grand Audience", event: "EdVantage" },
  { url: "/assets/images/gallery/gallery3.jpg", caption: "INSPIRE Agra - Panel Discussion", event: "INSPIRE" },
  { url: "/assets/images/gallery/gallery4.jpg", caption: "EdVantage - Award Ceremony", event: "EdVantage" },
  { url: "/assets/images/gallery/gallery5.jpg", caption: "AcadElite - Distinguished Guests", event: "AcadElite" },
  { url: "/assets/images/gallery/gallery6.jpg", caption: "INSPIRE Agra - Expert Panel", event: "INSPIRE" }
];

// Event IP Logos
const EVENT_LOGOS = {
  sparc: "/assets/images/logos/sparc.png",
  inspire: "/assets/images/logos/inspire.png",
  edvantage: "/assets/images/logos/edvantage.png",
  acadelite: "/assets/images/logos/acadelite.png"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// CountUp Component
const CountUp = ({ end, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return (
    <span ref={ref} className="gradient-text font-heading">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute top-1/4 -left-1/4 w-[500px] h-[500px] bg-yellow-400/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-red-500/5 rounded-full blur-[80px]" />

      <div className="container-custom relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="font-heading text-6xl md:text-8xl lg:text-9xl font-bold leading-[0.9] mb-6 tracking-tight" data-testid="hero-title">
            <span className="text-slate-800">Global School</span>
            <br />
            <span className="text-slate-800">Leaders'</span> <span className="gradient-text">Consortium</span>
          </h1>
          
          <p className="font-accent text-slate-500 text-base md:text-lg tracking-[0.2em] uppercase mb-8" data-testid="hero-subtitle">
            Connect | Collaborate | Contribute
          </p>

          <p className="font-body text-lg md:text-xl text-slate-700 max-w-4xl mx-auto mb-4 leading-relaxed font-medium" data-testid="hero-description">
            Shaping the Future of School Leadership
          </p>
          <p className="font-body text-base md:text-lg text-slate-600 max-w-4xl mx-auto mb-4 leading-relaxed">
            India's premier platform bringing together visionary school leaders, educators, and education partners to collaborate, learn, and lead change.
          </p>
          <p className="font-heading text-xl md:text-2xl text-red-600 italic max-w-3xl mx-auto mb-10">
            “One Network. Many Voices. One Future for Education.”
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/about"
                data-testid="hero-learn-btn"
                className="btn-gradient text-lg px-10 py-4 flex items-center justify-center gap-2 inline-flex"
              >
                Discover GSLC <ArrowRight size={20} />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="btn-outline-brand text-lg px-10 py-4 inline-flex items-center justify-center"
              >
                Get In Touch
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Company Description Section with Full Content
const CompanyDescription = () => {
  const whatWeDo = [
    {
      icon: Mic,
      title: "Leadership Conferences & Summits",
      description: "We organize flagship conferences and regional leadership conclaves across cities.",
      points: ["School Owners & Directors", "Principals & Academic Heads", "Policy Influencers & Education Experts", "EdTech & Education Solution Providers"]
    },
    {
      icon: Network,
      title: "Knowledge & Networking Platforms",
      description: "Our platforms are designed to enable meaningful connections.",
      points: ["Enable peer-to-peer learning among school leaders", "Showcase best practices in school management", "Create collaboration opportunities"]
    },
    {
      icon: Building2,
      title: "Strategic Partnerships",
      description: "We work closely with education brands and EdTech companies.",
      points: ["Reach the right school leadership audience", "Build credibility and long-term engagement", "Create meaningful impact in education"]
    }
  ];

  return (
    <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="max-w-5xl mx-auto text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-800 mb-8">
            About <span className="gradient-text">GSLC</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            In a world where education is the cornerstone of progress, the Global School Leaders' Consortium (GSLC) stands as a beacon of collaborative transformation. Founded in 2020 amid the upheavals of a global pandemic, GSLC was born from a profound realization: isolated excellence is fleeting, but collective synergy ignites enduring change.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            We are a vibrant community of over 14,000 school leaders, educators, and innovators dedicated to fostering idea exchange, best-practice sharing, and cooperative learning across borders. GSLC serves visionary School Leaders, EdTech pioneers, and policymakers who dare to reimagine classrooms as dynamic ecosystems of growth.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            Why does it matter? Because in an era of rapid technological flux and societal shifts, GSLC equips leaders to cultivate resilient, inclusive schools that empower the next generation, not just to learn, but to lead, innovate, and thrive.
          </p>
          <p className="text-xl text-red-600 font-heading italic">
            Join us in reshaping education from reaction to revolution.
          </p>
        </motion.div>

        <motion.div variants={fadeInUp} className="text-center mb-12">
          <h3 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
            What We <span className="gradient-text">Do</span>
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {whatWeDo.map((item, index) => (
            <motion.div key={index} variants={fadeInUp} className="card-light rounded-2xl p-8 text-center hover-lift">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-100 via-red-100 to-blue-100 flex items-center justify-center mx-auto mb-6">
                <item.icon className="w-10 h-10 text-red-600" />
              </div>
              <h4 className="font-heading text-xl text-slate-800 mb-3">{item.title}</h4>
              <p className="text-slate-600 text-sm mb-4">{item.description}</p>
              <ul className="text-left space-y-2">
                {item.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-500 text-sm">
                    <Check className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeInUp} className="text-center mt-10">
          <Link to="/about" className="text-blue-700 hover:text-red-600 transition-colors font-medium inline-flex items-center gap-2">
            Learn more about us <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Impact Stats Section with Separate Boxes
const ImpactStats = () => {
  const stats = [
    { value: 14, suffix: "k+", label: "School Leaders Engaged" },
    { value: 50, suffix: "+", label: "Cities Covered" },
    { value: 50, suffix: "+", label: "Conferences & Roundtables" },
    { value: 300, suffix: "+", label: "Webinars & Workshops" },
    { value: 300, suffix: "+", label: "Speakers & Experts" },
  ];

  return (
    <AnimatedSection className="section-padding">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Impact So Far</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 text-slate-800">
            Making a <span className="gradient-text">Difference</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <motion.div 
              key={index} 
              variants={fadeInUp} 
              className="card-light rounded-2xl p-6 text-center hover-lift"
            >
              <p className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-2">
                <CountUp end={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-slate-500 text-xs md:text-sm font-body">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Mission Section - Full Content Exactly as Provided
const MissionSection = () => {
  const missionPoints = [
    "Bring together educators, school leaders, policymakers, and education solution providers on one powerful platform",
    "Curate insightful conferences, summits, and learning experiences that address real challenges in schooling",
    "Enable knowledge-sharing, best practices, and future-focused leadership thinking",
    "Support schools in becoming more student-centric, future-ready, and globally competitive",
    "Build a strong community of progressive school leaders who drive meaningful change in education"
  ];

  return (
    <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
      <div className="container-custom">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Vision */}
          <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-100 to-red-100 flex items-center justify-center mb-6">
              <Target className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="font-heading text-2xl text-slate-800 mb-4">Our Vision</h3>
            <p className="text-slate-600 mb-4">
              To become India's most trusted and influential platform for school leadership, driving transformation in education 
              by empowering educators, leaders, and institutions to build future-ready schools.
            </p>
            <p className="text-slate-600 mb-4">
              We envision a future where every school leader is equipped with the right ideas, tools, and networks to:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                "Lead with innovation and purpose",
                "Build globally competitive, student-centric institutions",
                "Create impactful learning ecosystems",
                "Adapt confidently to the changing dynamics of education"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-500 text-sm">
                  <Check className="w-4 h-4 text-yellow-500" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-slate-500 text-sm italic">
              GSLC aims to be the catalyst that connects thought leaders, policymakers, educators, and solution providers 
              to collaboratively shape the future of schooling in India and beyond.
            </p>
          </motion.div>

          {/* Mission */}
          <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-100 to-blue-100 flex items-center justify-center mb-6">
              <Sparkles className="w-7 h-7 text-blue-700" />
            </div>
            <h3 className="font-heading text-2xl text-slate-800 mb-4">Our Mission</h3>
            <p className="text-slate-600 mb-4">
              GSLC's mission is to empower school leaders by creating high-impact platforms for learning, collaboration, and innovation.
            </p>
            <p className="text-slate-600 mb-4">We aim to:</p>
            <ul className="space-y-2 mb-4">
              {missionPoints.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-500 text-sm">
                  <Check className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-slate-500 text-sm italic">
              Through thought leadership, partnerships, and purposeful dialogue, GSLC is committed to strengthening 
              school leadership and transforming the education ecosystem in India.
            </p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Conclaves & Program IPs Preview - Updated with event info on hover/click
const ProgramIPsPreview = () => {
  const events = [
    { 
      id: "sparc", 
      name: "SPARC", 
      logo: EVENT_LOGOS.sparc, 
      fullName: "Symposium for Promoting Academic Research and Collaboration",
      description: "A distinguished intellectual platform bringing together visionary school leaders and educationists."
    },
    { 
      id: "acadelite", 
      name: "Acadelite", 
      logo: EVENT_LOGOS.acadelite, 
      fullName: "Celebrating Educational Excellence",
      description: "GSLC's flagship celebration honouring visionary school leaders and transformative institutions."
    },
    { 
      id: "inspire", 
      name: "INSPIRE", 
      logo: EVENT_LOGOS.inspire, 
      fullName: "International Summit for Planning and Incubating Reforms in Education",
      description: "A dynamic platform dedicated to igniting purpose, passion, and possibility within education."
    },
    { 
      id: "edvantage", 
      name: "Edvantage", 
      logo: EVENT_LOGOS.edvantage, 
      fullName: "Strategic Advantage for Future-Ready Schools",
      description: "A forward-looking platform empowering schools with strategic advantage in an evolving landscape."
    }
  ];

  return (
    <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Our Flagship Initiatives</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 text-slate-800">
            Conclaves & Program <span className="gradient-text">IPs</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {events.map((event, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <Link 
                to={`/learning-forums/${event.id}`}
                className="block card-light rounded-2xl p-6 text-center hover-lift h-full group"
              >
                <img 
                  src={event.logo} 
                  alt={event.name} 
                  className={`h-32 mx-auto mb-4 object-contain ${event.id === 'acadelite' ? '' : ''}`} 
                />
                <h3 className="font-heading text-xl text-slate-800 mb-1">{event.name}</h3>
                <p className="text-xs text-red-600 font-accent mb-3">{event.fullName}</p>
                <p className="text-slate-500 text-sm mb-3">
                  {event.description}
                </p>
                <span className="text-blue-700 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight size={14} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeInUp} className="text-center mt-10">
          <Link to="/learning-forums" className="btn-gradient inline-flex items-center gap-2">
            View All Learning Forums <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Roadmap Style Event Calendar with Checkered Flag
const EventCalendarRoadmap = () => {
  const events2026 = [
    { date: "14 Feb", name: "SPARC", location: "Jaipur", color: "bg-yellow-500", completed: true },
    { date: "25 Apr", name: "AcadElite", location: "Delhi", color: "bg-red-500", completed: false },
    { date: "23-25 Jul", name: "INSPIRE", location: "Uttarakhand", color: "bg-orange-500", completed: false },
    { date: "26 Sep", name: "EdVantage 4", location: "Bangalore", color: "bg-blue-500", completed: false },
    { date: "12 Dec", name: "EdVantage 5", location: "Pune", color: "bg-purple-500", completed: false },
  ];

  return (
    <AnimatedSection className="section-padding bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="font-accent text-yellow-400 text-sm tracking-[0.2em] uppercase">Mark Your Calendar</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 text-white">
            2026 Learning Forums <span className="text-yellow-400">Roadmap</span>
          </h2>
        </motion.div>

        {/* Roadmap Timeline */}
        <motion.div variants={fadeInUp} className="max-w-5xl mx-auto hidden md:block">
          {/* Timeline Line */}
          <div className="relative">
            <div className="absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-red-500 to-blue-500 rounded-full" />
            
            {/* Events on Timeline */}
            <div className="flex justify-between relative pt-4">
              {events2026.map((event, index) => (
                <div key={index} className="flex flex-col items-center relative" style={{ flex: 1 }}>
                  {/* Checkered Flag for Completed Events */}
                  {event.completed && (
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                      <Flag className="w-6 h-6 text-green-400" fill="currentColor" />
                    </div>
                  )}
                  
                  {/* Dot */}
                  <div className={`w-6 h-6 ${event.color} rounded-full border-4 border-white shadow-lg z-10 ${event.completed ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-slate-900' : ''}`} />
                  
                  {/* Event Card */}
                  <div className={`mt-6 ${index % 2 === 0 ? '' : 'mt-8'}`}>
                    <div className={`bg-white/10 backdrop-blur-sm rounded-xl p-4 border ${event.completed ? 'border-green-400/50' : 'border-white/20'} text-center min-w-[120px]`}>
                      <p className="text-yellow-400 font-accent font-bold text-lg">{event.date}</p>
                      <p className="text-white font-heading text-lg mt-1">{event.name}</p>
                      <p className="text-white/60 text-xs flex items-center justify-center gap-1 mt-1">
                        <MapPin size={10} />
                        {event.location}
                      </p>
                      {event.completed && (
                        <span className="inline-block mt-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                          Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Mobile View - Vertical Timeline */}
        <motion.div variants={fadeInUp} className="md:hidden">
          <div className="relative pl-8">
            {/* Vertical Line */}
            <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gradient-to-b from-yellow-500 via-red-500 to-blue-500" />
            
            {events2026.map((event, index) => (
              <div key={index} className="relative mb-8 last:mb-0">
                {event.completed && (
                  <Flag className="absolute -left-6 top-0 w-5 h-5 text-green-400" fill="currentColor" />
                )}
                <div className={`absolute left-0 -translate-x-1/2 w-4 h-4 ${event.color} rounded-full border-2 border-white ${event.completed ? 'ring-2 ring-green-400' : ''}`} />
                <div className={`bg-white/10 backdrop-blur-sm rounded-xl p-4 border ${event.completed ? 'border-green-400/50' : 'border-white/20'}`}>
                  <div className="flex items-center gap-2">
                    <p className="text-yellow-400 font-accent font-bold">{event.date}</p>
                    {event.completed && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Done</span>
                    )}
                  </div>
                  <p className="text-white font-heading text-lg">{event.name}</p>
                  <p className="text-white/60 text-sm flex items-center gap-1 mt-1">
                    <MapPin size={12} />
                    {event.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div variants={fadeInUp} className="text-center mt-12">
          <Link to="/learning-forums" className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-blue-900 font-semibold hover:bg-yellow-400 transition-colors">
            See Full Calendar <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Testimonials Section with Pictures and Scrolling - Proper Quotes
const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const testimonials = [
    {
      name: "Mr. J S Mann",
      role: "Chair",
      org: "Mann School",
      image: TESTIMONIAL_IMAGES.jsmann,
      quote: "I am truly honoured and humbled to receive the AcadElite EduTitan Award at the GSLC AcadElite Awards. My heartfelt gratitude to you, Pankaj, and your entire team for this recognition and for the impeccable organisation of such a grand and inspiring event."
    },
    {
      name: "Major General TPS Waraich, VSM & Bar (Retd)",
      role: "Director",
      org: "Yadavnidra Public School, Mohali",
      image: TESTIMONIAL_IMAGES.waraich,
      quote: "Thank you very much for organising a wonderful event, meticulously planned and executed. It was wonderful meeting all old friends and other eminent personalities. A special thank you to Pooja for the tremendous patience."
    },
    {
      name: "Ms. Keran Bahadur",
      role: "Chairperson",
      org: "Colonel's Academy, Mhow",
      image: TESTIMONIAL_IMAGES.keran,
      quote: "Thank you very much Pankaj, Pooja and team GSLC for the meticulously organised event. One could see the efforts gone in organising such a huge event. It was great meeting friends and stalwarts of Education World. Wishing you all a great success in your upcoming endeavours!"
    },
    {
      name: "Mr. Damodar Prasad Goyal",
      role: "President",
      org: "Society for Unaided Private Schools of Rajasthan",
      image: TESTIMONIAL_IMAGES.damodar,
      quote: "Congratulations for wonderful conference. It is your hard-work and love for education that attracted a large number of educators. Good discussions. Keep it up."
    },
    {
      name: "Mr. Neeraj Bedhotiya",
      role: "Exec. Director",
      org: "Sanskriti The School, Ajmer",
      image: TESTIMONIAL_IMAGES.neeraj,
      quote: "Congratulations on a successful conference today. Thought-provoking sessions provided a lot of opportunity for learning. We all know organising such a program requires diligence, time, and money. Kudos to GSLC."
    },
    {
      name: "Archana S. Mankotia",
      role: "Director",
      org: "Maharani Gayatri Devi Girls' School, Jaipur",
      image: TESTIMONIAL_IMAGES.archana,
      quote: "As always, a great initiative, passionately curated and lovingly executed. You provide us all a space to Pause, Reflect, and Learn — much needed for educators to usher in transformational changes. Your contribution to the education sphere is highly appreciated. God bless."
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Words & Appreciation</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 text-slate-800">
            What Leaders <span className="gradient-text">Say</span>
          </h2>
        </motion.div>

        {/* Carousel */}
        <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="card-light rounded-3xl p-8 md:p-12"
              >
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <img 
                      src={testimonials[currentIndex].image} 
                      alt={testimonials[currentIndex].name}
                      className="w-32 h-32 md:w-40 md:h-40 rounded-2xl object-cover shadow-lg"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-center md:text-left">
                    <Quote className="w-10 h-10 text-yellow-500 mb-4 mx-auto md:mx-0" />
                    <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                      “{testimonials[currentIndex].quote}”
                    </p>
                    <div>
                      <p className="text-slate-800 font-heading text-xl font-semibold">{testimonials[currentIndex].name}</p>
                      <p className="text-red-600">{testimonials[currentIndex].role}</p>
                      <p className="text-slate-500 text-sm">{testimonials[currentIndex].org}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 w-8' 
                      : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Image Carousel Section with Auto-scrolling
const ImageCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <AnimatedSection className="section-padding">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Memorable Moments</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 text-slate-800">
            Event <span className="gradient-text">Gallery</span>
          </h2>
        </motion.div>

        <motion.div variants={fadeInUp} className="relative max-w-5xl mx-auto">
          {/* Main Carousel */}
          <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <img
                  src={GALLERY_IMAGES[currentIndex].url}
                  alt={GALLERY_IMAGES[currentIndex].caption}
                  className="w-full h-full object-cover"
                />
                {/* Caption Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-heading text-lg md:text-xl">{GALLERY_IMAGES[currentIndex].caption}</p>
                  <span className="text-yellow-400 text-sm font-accent">{GALLERY_IMAGES[currentIndex].event}</span>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-slate-800 hover:bg-white transition-colors shadow-lg"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-slate-800 hover:bg-white transition-colors shadow-lg"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {GALLERY_IMAGES.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 w-8' 
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </motion.div>

        <motion.div variants={fadeInUp} className="text-center mt-10">
          <Link to="/gallery" className="btn-gradient inline-flex items-center gap-2">
            View Full Gallery <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Partners Section
const PartnersSection = () => {
  return (
    <AnimatedSection className="section-padding bg-gradient-to-br from-slate-100 via-white to-slate-100">
      <div className="container-custom">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Trusted By The Best</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium mt-4 text-slate-800">
            Our <span className="gradient-text">Partners</span>
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
            Leading brands in education and technology who partner with GSLC to transform school leadership
          </p>
        </motion.div>

        <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
          <img 
            src={PARTNERS_IMAGE} 
            alt="Our Partners - Leading education and technology brands" 
            className="w-full rounded-2xl"
          />
        </motion.div>

        <motion.div variants={fadeInUp} className="text-center mt-10">
          <Link to="/partners" className="btn-gradient inline-flex items-center gap-2">
            <Handshake size={18} />
            Partner With Us <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <AnimatedSection className="section-padding">
      <div className="container-custom">
        <motion.div 
          variants={fadeInUp}
          className="bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-black/10" />
          <div className="relative z-10">
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-white mb-6">
              Ready to Transform Education?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Connect with India's largest community of school leaders and be part of the change.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-blue-900 font-semibold text-lg hover:bg-yellow-400 transition-colors shadow-lg"
            >
              Get In Touch <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <>
      <Hero />
      <CompanyDescription />
      <ImpactStats />
      <MissionSection />
      <ProgramIPsPreview />
      <ImageCarousel />
      <EventCalendarRoadmap />
      <PartnersSection />
      <Testimonials />
      <NewsletterSignup />
      <CTASection />
    </>
  );
};

export default HomePage;
